import React, { useEffect, useMemo, useState } from 'react'
import { getObConfig, type ObConfig, type Onboarding } from './crmApi'
import { Card, Chip, Eyebrow, BtnSoft, BtnOutline, OB, PATHWAY_COLORS, LockIcon } from './obDesign'

/**
 * PATHWAYS TAB — built to Natalie's mockups CoPathways2 (Journey view) and
 * CoPathways (Checklist view). Pathways group the live stages: CRM & Data
 * Import → "Data migration", Bespoke Proposal → "Proposal set-up",
 * everything else → "Training & Setup". Stations are the live stages with
 * their checkpoints EXACTLY as live (the pack's hard rule) — only the
 * presentation is the mockup's journey rail / accordion checklist.
 */

const CALENDLY_URL = 'https://calendly.com/wequote/onboarding-training-call'

type StState = 'd' | 'c' | 'w' | 't' | 'l'
const ST_LABEL: Record<StState, [string, string]> = {
  d: ['Done', OB.green], c: ['Current', OB.accent], w: ['Waiting on them', OB.amber],
  t: ['Up next', OB.muted], l: ['Locked', OB.faint],
}
const KIND_STYLE: Record<string, { bg: string; fg: string }> = {
  AUTO: { bg: OB.greenBg, fg: OB.green }, BOOK: { bg: '#E4EDFC', fg: OB.accent },
  CALL: { bg: OB.purpleBg, fg: OB.purple }, MANUAL: { bg: OB.track, fg: OB.muted },
}

const pathwayOf = (short: string): string =>
  /crm/i.test(short) ? 'Data migration' : /bespoke/i.test(short) ? 'Proposal set-up' : 'Training & Setup'

interface Station {
  idx: number; name: string; short: string; state: StState; lockMsg: string
  done: number; total: number; heldSessions: Array<{ label: string; at: string | null }>
  notes: string[]; ours: Array<{ who: string; task: string }>; theirs: Array<{ who: string; task: string }>
  items: Array<{ text: string; kind: string; done: boolean; extra: string }>
  next: string
}
interface Pathway { name: string; color: string; lead: string; stations: Station[]; summary: string }

export const ObPathwaysTab: React.FC<{ ob: Onboarding & Record<string, any>; deal: any; owner: string }> = ({ ob, deal, owner }) => {
  const [cfg, setCfg] = useState<ObConfig | null>(null)
  const [view, setView] = useState<'journey' | 'checklist'>('journey')
  const [sel, setSel] = useState<number | null>(null)
  const [open, setOpen] = useState<Record<number, boolean>>({})
  const [filter, setFilter] = useState('All')
  const [aboutOpen, setAboutOpen] = useState(true)
  useEffect(() => { getObConfig().then(setCfg).catch(() => {}) }, [])

  const pathways: Pathway[] = useMemo(() => {
    if (!cfg) return []
    const stepDone = new Map<string, boolean>()
    for (const s of ob.steps || []) stepDone.set(`${s.stageIndex}.${s.stepIndex}`, s.done)
    const tasks: any[] = (deal?.tasks || []).filter((t: any) => !t.done && !t.isDefault)
    const meetings: any[] = ob.meetings || []
    const map = new Map<string, Station[]>()
    cfg.stages.forEach((S, i) => {
      const row: any = (ob.stages || [])[i] || {}
      if (row.hidden) return
      const held = meetings.filter((m) => m.stageIndex === i && m.status === 'held')
      const stTasks = tasks.filter((t) => t.station && (new RegExp(S.short, 'i').test(t.station) || new RegExp(t.station.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(S.name)))
      const theirs = stTasks.filter((t) => (t.side || 'ours') === 'theirs').map((t) => ({ who: t.ownerName || 'Customer', task: t.title }))
      const ours = stTasks.filter((t) => (t.side || 'ours') === 'ours').map((t) => ({ who: t.ownerName || 'No owner', task: t.title }))
      const overdueTheirs = stTasks.some((t) => (t.side || 'ours') === 'theirs' && t.due != null && t.due < 0)
      const state: StState = row.complete ? 'd' : row.locked ? 'l' : i === ob.currentStage ? (overdueTheirs ? 'w' : 'c') : 't'
      const items = S.steps.map((st, pi) => ({
        text: st.t, kind: st.tag || 'MANUAL', done: !!stepDone.get(`${i}.${pi}`),
        extra: st.note || (st.counter ? '' : '') || '',
      }))
      const notes = held.filter((m) => m.summary).slice(-2).map((m) => `${m.startAt ? new Date(m.startAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''} · ${m.summary}`.replace(/^\s·\s/, ''))
      const firstOpen = items.find((x) => !x.done)
      const st: Station = {
        idx: i, name: S.name, short: S.short, state,
        lockMsg: row.lockMsg || (S.unlock === 'three' ? 'Opens after 3 quotes' : S.unlock === 'accepted' ? 'Opens after an accepted quote' : 'Locked'),
        done: items.filter((x) => x.done).length, total: items.length,
        heldSessions: held.map((m) => ({ label: `${m.subject || 'Session'} · ${m.startAt ? new Date(m.startAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}`, at: m.startAt })),
        notes, ours, theirs, items,
        next: row.complete ? 'Done' : row.locked ? (row.lockMsg || 'Locked') : firstOpen ? firstOpen.text : 'All checkpoints done',
      }
      const p = pathwayOf(S.short)
      if (!map.has(p)) map.set(p, [])
      map.get(p)!.push(st)
    })
    const order = ['Training & Setup', 'Data migration', 'Proposal set-up']
    return order.filter((n) => map.has(n)).map((n) => {
      const stations = map.get(n)!
      const doneN = stations.filter((s) => s.state === 'd').length
      return { name: n, color: PATHWAY_COLORS[n] || OB.accent, lead: owner, stations, summary: `${doneN} of ${stations.length} stops done` }
    })
  }, [cfg, ob, deal, owner])

  const allStations = pathways.flatMap((p) => p.stations.map((s) => ({ ...s, path: p.name, color: p.color })))
  const selected = allStations.find((s) => s.idx === sel) || allStations.find((s) => s.state === 'c') || allStations[0]

  if (!cfg) return <p style={{ fontSize: 12.5, color: OB.muted }}>Loading…</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: OB.font }}>
      {/* view toggle */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div role="group" aria-label="View" style={{ display: 'flex', background: OB.surface, border: `1px solid ${OB.border}`, borderRadius: 10, padding: 3 }}>
          {([['journey', 'Journey'], ['checklist', 'Checklist']] as const).map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} style={{ font: 'inherit', fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '6px 14px', border: 0, cursor: 'pointer', background: view === k ? OB.accent : 'transparent', color: view === k ? '#fff' : OB.ink2 }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: 12.5, color: OB.muted }}>
          {view === 'journey' ? 'Journey: the big stops on each pathway. Switch to Checklist for every step.' : 'Checklist: every checkpoint, session, note and task per station'}
        </span>
      </div>

      {view === 'journey' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          {/* pathway columns */}
          <div style={{ flex: '999 1 640px', minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            {pathways.map((p) => (
              <Card key={p.name} pad="16px 16px 8px" gap={0}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: 12 }}>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ width: 11, height: 11, borderRadius: '50%', display: 'inline-block', background: p.color }} />
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{p.name}</span>
                  </span>
                  <span style={{ fontSize: 12.5, color: OB.muted }}>Lead: {p.lead} · {p.summary}</span>
                </div>
                {p.stations.map((s, i) => {
                  const first = i === 0, last = i === p.stations.length - 1
                  const isSel = selected && s.idx === selected.idx
                  const nextOn = !last && (p.stations[i + 1].state === 'd' || p.stations[i + 1].state === 'c' || p.stations[i + 1].state === 'w')
                  const dotStyle: React.CSSProperties =
                    s.state === 'd' ? { background: p.color, color: '#fff' }
                    : s.state === 'c' ? { background: '#fff', border: `5px solid ${p.color}` }
                    : s.state === 'w' ? { background: '#fff', border: `5px solid ${OB.amberDot}` }
                    : { background: '#fff', border: `3px solid ${OB.inputBorder}`, color: OB.faint }
                  const stateLabel = s.state === 'l' ? 'Locked' : ST_LABEL[s.state][0]
                  return (
                    <button key={s.idx} onClick={() => setSel(s.idx)} style={{ font: 'inherit', background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', display: 'grid', gridTemplateColumns: '34px 1fr', gap: 10, color: OB.ink }}>
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ width: 4, height: 14, background: first ? 'transparent' : (s.state === 't' || s.state === 'l' ? OB.lockBorder : p.color) }} />
                        <span style={{ width: 26, height: 26, borderRadius: '50%', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flex: '0 0 26px', ...dotStyle }}>{s.state === 'd' ? '✓' : s.state === 'l' ? <LockIcon color={OB.faint} /> : ''}</span>
                        <span style={{ width: 4, flex: 1, minHeight: 30, background: last ? 'transparent' : (nextOn ? p.color : OB.lockBorder) }} />
                      </span>
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, borderRadius: 10, padding: '8px 10px', margin: '6px 0 10px', background: isSel ? OB.accentPale : OB.headerBg, border: `1.5px solid ${isSel ? OB.accent : 'transparent'}` }}>
                        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline' }}>
                          <span style={{ fontSize: 14, fontWeight: 800, minWidth: 0 }}>{s.name}</span>
                          <span title={s.state === 'l' ? s.lockMsg : undefined} style={{ fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0, color: s.state === 'l' ? OB.faint : ST_LABEL[s.state][1] }}>{stateLabel}</span>
                        </span>
                        <span style={{ fontSize: 12.5, color: OB.ink2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.next}</span>
                        <span style={{ display: 'flex', gap: 10, fontSize: 12, color: OB.muted, flexWrap: 'wrap' }}>
                          <span>{s.heldSessions.length ? `${s.heldSessions.length} session${s.heldSessions.length === 1 ? '' : 's'}` : 'No sessions'}</span>
                          <span>{s.ours.length + s.theirs.length ? `${s.ours.length + s.theirs.length} open tasks` : 'No open tasks'}</span>
                        </span>
                      </span>
                    </button>
                  )
                })}
              </Card>
            ))}
          </div>
          {/* sticky inspector */}
          {selected && (
            <aside style={{ flex: '1 1 300px', minWidth: 0, position: 'sticky', top: 16 }}>
              <Card>
                <Eyebrow>{(selected as any).path} · lead {owner}</Eyebrow>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 17, fontWeight: 800 }}>{selected.name}</span>
                  <span title={selected.state === 'l' ? selected.lockMsg : undefined} style={{ fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0, color: selected.state === 'l' ? OB.faint : ST_LABEL[selected.state][1] }}>{selected.state === 'l' ? 'Locked' : ST_LABEL[selected.state][0]}</span>
                </div>
                <span style={{ fontSize: 12.5, color: OB.muted }}>{selected.done} of {selected.total} checkpoints</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Eyebrow>Sessions</Eyebrow>
                  {selected.heldSessions.map((se, i) => <span key={i} style={{ fontSize: 13 }}>{se.label}</span>)}
                  {!selected.heldSessions.length && <span style={{ fontSize: 12.5, color: OB.faint }}>No sessions yet</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Eyebrow>Tasks</Eyebrow>
                  {selected.ours.slice(0, 3).map((t, i) => <span key={`o${i}`} style={{ fontSize: 13 }}><strong>Us · {t.who}:</strong> {t.task}</span>)}
                  {selected.theirs.slice(0, 3).map((t, i) => <span key={`t${i}`} style={{ fontSize: 13 }}><strong>Them · {t.who}:</strong> {t.task}</span>)}
                  {!selected.ours.length && !selected.theirs.length && <span style={{ fontSize: 12.5, color: OB.faint }}>No open tasks</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Eyebrow>Latest note</Eyebrow>
                  <span style={{ fontSize: 13, color: OB.ink2 }}>{selected.notes[0] || 'No notes yet'}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <BtnSoft onClick={() => window.open(CALENDLY_URL, '_blank', 'noopener')}>Book a session</BtnSoft>
                  <BtnOutline onClick={() => { setView('checklist'); setOpen({ [selected.idx]: true }) }}>See every step →</BtnOutline>
                </div>
              </Card>
            </aside>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: '999 1 640px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* pathway filter chips */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['All', ...pathways.map((p) => p.name)].map((l) => <Chip key={l} on={filter === l} onClick={() => setFilter(l)}>{l}</Chip>)}
            </div>
            {pathways.filter((p) => filter === 'All' || p.name === filter).map((p) => (
              <Card key={p.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ width: 11, height: 11, borderRadius: '50%', display: 'inline-block', background: p.color }} />
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{p.name}</span>
                    <span style={{ fontSize: 12.5, color: OB.muted }}>Lead: {p.lead}</span>
                  </div>
                  <span style={{ fontSize: 12.5, color: OB.muted }}>{p.summary}</span>
                </div>
                {/* segment bar */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {p.stations.map((s) => (
                    <span key={s.idx} title={s.name + ': ' + (s.state === 'l' ? s.lockMsg : ST_LABEL[s.state][0])} style={{ flex: 1, height: 8, borderRadius: 4, background: s.state === 'd' ? OB.greenDot : s.state === 'c' ? OB.accent : s.state === 'w' ? OB.amberDot : OB.border }} />
                  ))}
                </div>
                {p.stations.map((s) => {
                  const isOpen = !!open[s.idx]
                  const iconStyle: React.CSSProperties =
                    s.state === 'd' ? { background: OB.greenDot, color: '#fff' }
                    : s.state === 'c' ? { background: OB.accentSoft, border: `2.5px solid ${OB.accent}` }
                    : s.state === 'w' ? { background: OB.amberBg, border: `2.5px solid ${OB.amberRing}` }
                    : s.state === 'l' ? { background: OB.track, color: OB.faint }
                    : { background: '#fff', border: `1.5px solid ${OB.faint2}` }
                  return (
                    <div key={s.idx} style={{ borderTop: `1px solid ${OB.rowDiv}`, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button onClick={() => setOpen({ ...open, [s.idx]: !isOpen })} style={{ font: 'inherit', background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', display: 'grid', gridTemplateColumns: '22px 1fr auto auto 18px', gap: 10, alignItems: 'center', color: OB.ink }}>
                        <span style={{ width: 20, height: 20, borderRadius: '50%', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, ...iconStyle }}>{s.state === 'd' ? '✓' : s.state === 'l' ? <LockIcon size={10} color={OB.faint} /> : ''}</span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 800 }}>{s.name}</span>
                          <span style={{ fontSize: 12, color: OB.muted }}>{s.heldSessions.length ? `${s.heldSessions.length} session${s.heldSessions.length === 1 ? '' : 's'} held` : 'No sessions yet'}</span>
                        </span>
                        <span title={s.state === 'l' ? s.lockMsg : undefined} style={{ fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', color: s.state === 'l' ? OB.faint : ST_LABEL[s.state][1] }}>{s.state === 'l' ? 'Locked' : ST_LABEL[s.state][0]}</span>
                        <span style={{ fontSize: 12.5, color: OB.muted, whiteSpace: 'nowrap' }}>{s.done} / {s.total} checkpoints</span>
                        <span style={{ color: OB.accent, fontWeight: 800 }}>{isOpen ? '▾' : '▸'}</span>
                      </button>
                      {isOpen && (
                        <div style={{ background: OB.headerBg, borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <Eyebrow>Checkpoints · {s.done} / {s.total}</Eyebrow>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '6px 18px' }}>
                              {s.items.map((it, i) => (
                                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
                                  <span style={{ width: 16, height: 16, borderRadius: 4, flex: '0 0 16px', marginTop: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, boxSizing: 'border-box', ...(it.done ? { background: OB.greenDot, color: '#fff' } : { border: `1.5px solid ${OB.faint2}` }) }}>{it.done ? '✓' : ''}</span>
                                  <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <span style={{ color: it.done ? OB.muted : OB.ink, textDecoration: it.done ? 'line-through' : 'none' }}>{it.text}</span>
                                    {it.extra && <span style={{ fontSize: 12, color: OB.muted }}>{it.extra}</span>}
                                  </span>
                                  <span style={{ fontSize: 10.5, fontWeight: 800, borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap', background: (KIND_STYLE[it.kind] || KIND_STYLE.MANUAL).bg, color: (KIND_STYLE[it.kind] || KIND_STYLE.MANUAL).fg }}>{it.kind}</span>
                                </div>
                              ))}
                            </div>
                            <span style={{ fontSize: 12, color: OB.muted }}>Ticking off, Go to page ↗, tutorials and Add file / link live on the classic checklist — open it from ··· in the header.</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, borderTop: `1px solid ${OB.border}`, paddingTop: 12 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <Eyebrow>Sessions · {s.heldSessions.length}</Eyebrow>
                              {s.heldSessions.map((se, i) => <span key={i} style={{ fontSize: 13, background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 8, padding: '6px 9px' }}>{se.label}</span>)}
                              {!s.heldSessions.length && <span style={{ fontSize: 12.5, color: OB.faint }}>No sessions yet</span>}
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <BtnSoft onClick={() => window.open(CALENDLY_URL, '_blank', 'noopener')}>Book a session</BtnSoft>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <Eyebrow>Notes · {s.notes.length}</Eyebrow>
                              {s.notes.map((n, i) => <span key={i} style={{ fontSize: 13, color: OB.ink2, background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 8, padding: '6px 9px' }}>{n}</span>)}
                              {!s.notes.length && <span style={{ fontSize: 12.5, color: OB.faint }}>No notes yet</span>}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <Eyebrow>Tasks · ours {s.ours.length} · theirs {s.theirs.length}</Eyebrow>
                              {s.ours.slice(0, 3).map((t, i) => (
                                <span key={`o${i}`} style={{ fontSize: 13, background: '#fff', border: `1px solid ${OB.border}`, borderLeft: `3px solid ${t.who === 'No owner' ? OB.red : OB.accent}`, borderRadius: 8, padding: '6px 9px', color: t.who === 'No owner' ? OB.red : OB.ink }}><strong>Us · {t.who}:</strong> {t.task}</span>
                              ))}
                              {s.theirs.slice(0, 3).map((t, i) => (
                                <span key={`t${i}`} style={{ fontSize: 13, background: '#fff', border: `1px solid ${OB.border}`, borderLeft: '3px solid #2F4A6B', borderRadius: 8, padding: '6px 9px' }}><strong>Them · {t.who}:</strong> {t.task}</span>
                              ))}
                              {!s.ours.length && !s.theirs.length && <span style={{ fontSize: 12.5, color: OB.faint }}>No open tasks</span>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </Card>
            ))}
          </div>
          {/* About the business */}
          <aside style={{ flex: '1 1 240px', maxWidth: 300, minWidth: 0 }}>
            <Card pad="12px 14px" gap={8}>
              <button onClick={() => setAboutOpen(!aboutOpen)} style={{ font: 'inherit', background: 'none', border: 0, padding: 0, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, color: OB.ink, textAlign: 'left' }}>
                <span style={{ fontSize: 14, fontWeight: 800 }}>About the business</span>
                <span style={{ color: OB.accent, fontWeight: 800 }}>{aboutOpen ? '▾' : '▸'}</span>
              </button>
              {aboutOpen && ([
                ['MODULES', [ob.mods?.wh && 'WorkHub', ob.mods?.pr && 'Procurement & Inventory', ob.mods?.bp && 'Bespoke Proposal'].filter(Boolean).join(' · ') || 'None selected'],
                ['TEAM', [ob.biz?.sales != null && `${ob.biz.sales} sales`, ob.biz?.techs != null && `${ob.biz.techs} technicians`].filter(Boolean).join(' · ') || 'Not recorded'],
                ['MOVING FROM', ob.biz?.inv || 'Not recorded'],
              ] as Array<[string, string]>).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 1, borderTop: `1px solid ${OB.rowDiv}`, paddingTop: 6 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', color: OB.faint }}>{k}</span>
                  <span style={{ fontSize: 13 }}>{v}</span>
                </div>
              ))}
            </Card>
          </aside>
        </div>
      )}
    </div>
  )
}
export default ObPathwaysTab
